// /src/GuitarSimulator/ExternalInputControls.js

import React, { useState, useEffect } from 'react';
import './ExternalInputControls.css';

const ExternalInputControls = ({ 
  inputSource, 
  isExternalInput, 
  language,
  audioAnalyzer,
  onMicrophoneToggle, 
  onFileUpload, 
  onStopInput 
}) => {
  
  const [fileInfo, setFileInfo] = useState(null);
  const [fileVolume, setFileVolume] = useState(50);
  const [sensitivity, setSensitivity] = useState(10); // 민감도 설정

  // 파일 정보 업데이트
  useEffect(() => {
    if (audioAnalyzer && inputSource === 'file') {
      const updateFileInfo = () => {
        const info = audioAnalyzer.getFileInfo();
        if (info) {
          setFileInfo(info);
          setFileVolume(info.volume);
        }
      };

      updateFileInfo();
      const interval = setInterval(updateFileInfo, 1000);
      
      return () => clearInterval(interval);
    } else {
      setFileInfo(null);
    }
  }, [audioAnalyzer, inputSource]);

  // 민감도 변경 시 분석기에 적용
  useEffect(() => {
    if (audioAnalyzer && audioAnalyzer.setSensitivity) {
      audioAnalyzer.setSensitivity(sensitivity);
    }
  }, [audioAnalyzer, sensitivity]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('audio/')) {
      onFileUpload(file);
    } else {
      alert(language === 'KR' ? '오디오 파일만 업로드 가능합니다.' : 'Only audio files are supported.');
    }
    event.target.value = '';
  };

  const handleFileVolumeChange = (event) => {
    const volume = parseInt(event.target.value);
    setFileVolume(volume);
    if (audioAnalyzer) {
      audioAnalyzer.setFileVolume(volume);
    }
  };

  const handleSensitivityChange = (event) => {
    const newSensitivity = parseInt(event.target.value);
    setSensitivity(newSensitivity);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (event) => {
    if (audioAnalyzer && fileInfo) {
      const percent = event.target.value / 100;
      const seekTime = fileInfo.duration * percent;
      audioAnalyzer.seekFile(seekTime);
    }
  };

  return (
    <div className="external-input-controls">
      <div className="input-buttons">
        <>
        <button
          onClick={onMicrophoneToggle}
          className={`input-btn ${inputSource === 'microphone' ? 'active' : ''}`}
          disabled={inputSource === 'file'}
          style={{width: '150px'}}
        >
          🎤 {inputSource === 'microphone' 
            ? (language === 'KR' ? '마이크 중지' : 'Stop Microphone')
            : (language === 'KR' ? '마이크 입력' : 'Mic. Input')
          }
        </button>
        </>
        
        <>
        <label className="file-input-label">
          <input
            type="file"
            accept="audio/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={isExternalInput}
          />
          <span className={`input-btn ${inputSource === 'file' ? 'active' : ''} ${isExternalInput ? 'disabled' : ''}`}>
            📁 {language === 'KR' ? '파일 업로드' : 'Upload File'}
          </span>
        </label>
        </>
        
        {isExternalInput && (
          <button onClick={onStopInput} className="input-btn stop-btn">
            ⏹️ {language === 'KR' ? '입력 중지' : 'Stop Input'}
          </button>
        )}
      </div>

      {/* 민감도 조절 */}
      {isExternalInput && (
        <div className="sensitivity-control">
          <span>{language === 'KR' ? '🎚️ 민감도:' : '🎚️ Sensitivity:'}</span>
          <input
            type="range"
            min="0"
            max="90"
            value={sensitivity}
            onChange={handleSensitivityChange}
            className="sensitivity-slider"
          />
          <span>{sensitivity}%</span>
          <small style={{marginLeft: '10px', color: '#666'}}>
            {language === 'KR' ? 
              (sensitivity < 30 ? '낮음 (강한 신호만)' : sensitivity < 70 ? '보통' : '높음 (작은 신호도)') :
              (sensitivity < 30 ? 'Low (strong signals only)' : sensitivity < 70 ? 'Normal' : 'High (weak signals too)')
            }
          </small>
        </div>
      )}

      {/* 파일 재생 컨트롤 */}
      {inputSource === 'file' && fileInfo && (
        <div className="file-player-controls">
          <div className="player-header">
            <span className="player-title">
              🎵 {language === 'KR' ? '파일 재생 중' : 'Playing File'}
            </span>
          </div>
          
          <div className="player-main">
            <div className="play-pause-controls">
              <button
                onClick={() => fileInfo.paused ? audioAnalyzer.resumeFile() : audioAnalyzer.pauseFile()}
                className="play-pause-btn"
              >
                {fileInfo.paused ? '▶️' : '⏸️'}
              </button>
            </div>
            
            <div className="progress-section">
              <div className="time-display">
                <span>{formatTime(fileInfo.currentTime)}</span>
                <span>/</span>
                <span>{formatTime(fileInfo.duration)}</span>
              </div>
              
              <input
                type="range"
                min="0"
                max="100"
                value={fileInfo.duration ? (fileInfo.currentTime / fileInfo.duration) * 100 : 0}
                onChange={handleSeek}
                className="progress-slider"
              />
            </div>
            
            <div className="volume-section">
              <span>🔊</span>
              <input
                type="range"
                min="0"
                max="100"
                value={fileVolume}
                onChange={handleFileVolumeChange}
                className="volume-slider"
              />
              <span>{fileVolume}%</span>
            </div>
          </div>
        </div>
      )}
      
      {isExternalInput && (
        <div className="input-status">
          🔴 {language === 'KR' ? '외부 입력 활성' : 'External Input Active'}: {
            inputSource === 'microphone' 
              ? (language === 'KR' ? '마이크' : 'Microphone')
              : (language === 'KR' ? '파일' : 'File')
          }
        </div>
      )}
      
      <div className="input-instructions">
        <small>
          {language === 'KR' 
            ? '• 마이크: 실시간 기타 연주를 분석합니다'
            : '• Microphone: Analyze real-time guitar playing'
          }<br/>
          {language === 'KR' 
            ? '• 파일: 오디오 파일을 재생하며 주파수를 분석합니다'
            : '• File: Play audio file while analyzing frequencies'
          }
        </small>
        
        {/* 마이크 문제 해결 도움말 */}
        <div className="help-text">
          <strong>{language === 'KR' ? '마이크 문제 해결:' : 'Microphone Troubleshooting:'}</strong><br/>
          {language === 'KR' 
            ? '1. 브라우저 주소창 왼쪽의 마이크 아이콘을 클릭하여 권한 허용'
            : '1. Click microphone icon in browser address bar to allow permission'
          }<br/>
          {language === 'KR' 
            ? '2. 브라우저 설정 → 개인정보 보호 → 마이크 권한 확인'
            : '2. Browser Settings → Privacy → Check microphone permissions'
          }<br/>
          {language === 'KR' 
            ? '3. 시스템 설정에서 마이크가 활성화되어 있는지 확인'
            : '3. Check if microphone is enabled in system settings'
          }
        </div>
        
        {/* 파일 재생 문제 해결 도움말 */}
        {inputSource === 'file' && (
          <div className="input-warning">
            <strong>{language === 'KR' ? '파일 재생 문제 해결:' : 'File Playback Troubleshooting:'}</strong><br/>
            {language === 'KR' 
              ? '• 소리가 안 들리면: 플레이어의 재생 버튼(▶️)을 클릭하세요'
              : '• No sound: Click the play button (▶️) in the player'
            }<br/>
            {language === 'KR' 
              ? '• 볼륨을 확인하고 시스템 볼륨도 확인하세요'
              : '• Check volume settings and system volume'
            }<br/>
            {language === 'KR' 
              ? '• 지원 형식: MP3, WAV, M4A, OGG'
              : '• Supported formats: MP3, WAV, M4A, OGG'
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalInputControls;